/* eslint-disable no-console */
import {
  useGetCashSaleStatusLazyQuery,
  useGetOrderDataLazyQuery,
  useOrderViewDataQuery,
  useSyncCipOrderMutation
} from "@saleor/graphql";
import { UseNavigatorResult } from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { FormikProps } from "formik";
import { Dispatch, SetStateAction, useState } from "react";

import { getNotRefCreateOrderPath } from "../urls";
import {
  checkIsCashSaleOrder,
  clearLocalOrderIds,
  getLocalOrderId
} from "../utils";

export interface ICreateOrChangeOrderFetchProps {
  orderId: string;
  setShowDetails: Dispatch<SetStateAction<boolean>>;
  navigate: UseNavigatorResult;
  isChangeFlow: boolean;
  state?: string;
}

function useCreateOrChangeOrderFetch({
  orderId,
  setShowDetails,
  navigate,
  isChangeFlow,
  state
}: ICreateOrChangeOrderFetchProps) {
  const notify = useNotifier();
  const [isSynced, setIsSynced] = useState<boolean>(false);
  const [callResetItemDetails, setCallResetItemDetails] = useState<boolean>(
    false
  );
  const [shouldRecheckBlock, setShouldRecheckBlock] = useState<boolean>(false);
  const [refetchInfo, setRefetchInfo] = useState({
    refetch: false,
    soldTo: "",
    saleOrg: "",
    distributionChannel: "",
    division: "",
    shipTo: ""
  });
  const [totalItemsOriginal, setTotalItemsOriginal] = useState<number>(0);

  function goToNotRefCreateNewOrder() {
    clearLocalOrderIds();
    navigate(getNotRefCreateOrderPath(""), {
      replace: true
    });
  }

  function checkIsNonReusableOrder(orderInfo: string) {
    return ["processing", "done"].includes(orderInfo);
  }

  // create and copy order fetch
  const [
    fetchOrderData,
    { data: getOrderData, loading: loadingGetOrder }
  ] = useGetOrderDataLazyQuery({
    fetchPolicy: "no-cache",
    onCompleted(data) {
      const soldToCode = data?.getOrderData?.customer?.soldToCode;
      const orderInfo = data?.getOrderData?.info;
      const localId = getLocalOrderId(soldToCode);

      setShouldRecheckBlock(false);
      const isNonReusableOrder = checkIsNonReusableOrder(orderInfo);
      if (isNonReusableOrder) {
        if (!orderId && localId && state === "idle") {
          clearLocalOrderIds();
          setShowDetails(true);
          setTimeout(() => setShouldRecheckBlock(true));
          return;
        }
        if (orderId || ["submitted", "timeout"].includes(state)) {
          goToNotRefCreateNewOrder();
          return;
        }
      }

      setShowDetails(true);
      if (!orderId) {
        navigate(getNotRefCreateOrderPath(data?.getOrderData?.id), {
          replace: true
        });
      }
      setRefetchInfo({
        refetch: true,
        soldTo: data?.getOrderData?.customer?.soldToCode,
        saleOrg: data?.getOrderData?.saleOrganization.code,
        distributionChannel: data?.getOrderData?.distributionChannel?.code,
        division: data?.getOrderData?.division?.code,
        shipTo: data?.getOrderData?.shipTo?.split("-")?.[0]?.trim()
      });
    },
    onError() {
      goToNotRefCreateNewOrder();
    }
  });

  // cash sale status fetch
  const [
    getCashSaleStatus,
    {
      data: cashSaleStatusData,
      loading: loadingGetCashSaleStatus,
      error: cashSaleStatusError
    }
  ] = useGetCashSaleStatusLazyQuery({
    fetchPolicy: "no-cache"
  });

  const fetchCashSaleStatus = (soNo: string, orderType: string) => {
    const isCashSaleOrder = checkIsCashSaleOrder(orderType);
    if (!isCashSaleOrder || !soNo) {
      return;
    }
    getCashSaleStatus({
      variables: {
        soNo
      }
    });
  };

  // change order fetch
  const {
    data: changeOrderData,
    loading: loadingChangeData,
    refetch: refetchChangeOrder
  } = useOrderViewDataQuery({
    skip: !orderId || !isChangeFlow,
    variables: {
      soNo: orderId
    },
    fetchPolicy: "no-cache",
    onCompleted: data => {
      const { orderViewData } = data ?? {};

      setRefetchInfo({
        refetch: true,
        soldTo: orderViewData?.soldToCode,
        saleOrg: orderViewData?.salesOrgCode,
        distributionChannel: orderViewData?.distributionChannelCode,
        division: orderViewData?.divisionCode,
        shipTo: orderViewData?.shipToDisplayText?.split("-")?.[0]?.trim()
      });
      setIsSynced(false);
      setTotalItemsOriginal(orderViewData?.orderItems?.length);

      fetchCashSaleStatus(orderViewData?.soNo, orderViewData?.orderType);
    }
  });

  // sync order
  const [callSyncCipOrder, { loading: loadingSync }] = useSyncCipOrderMutation({
    variables: { soNo: orderId },
    fetchPolicy: "no-cache",
    onError: error => {
      notify({
        status: "error",
        apiMessage: error.message
      });
    }
  });

  function mapSync(lines, items) {
    return lines.map((line, i) => ({
      ...line,
      id: items[i].id,
      parentId: items[i].parentId,
      bomUnitQuantity: items[i].bomUnitQuantity
    }));
  }

  function syncCipOrder(
    formikHeader: FormikProps<NotRefOrderHeader>,
    formikItemDetails: FormikProps<any>
  ) {
    if (isSynced) {
      return Promise.resolve(true);
    }
    return callSyncCipOrder()
      .then(({ data, errors }) => {
        if (data.syncCipOrder.order.id) {
          const items = data.syncCipOrder.order.orderLines;
          setIsSynced(true);
          formikHeader.setFieldValue("id", data.syncCipOrder.order.id);
          formikItemDetails.setValues(prev => ({
            ...prev,
            lines: [...mapSync(prev.lines, items)]
          }));
          return true;
        }
        if (data?.syncCipOrder?.errors?.length) {
          return false;
        }
        if (errors?.length) {
          return false;
        }
        return false;
      })
      .catch(() => false);
  }

  async function refetchChangeOrderData() {
    return refetchChangeOrder().then(result => {
      const { orderViewData } = result?.data ?? {};

      setRefetchInfo({
        refetch: true,
        soldTo: orderViewData?.soldToCode,
        saleOrg: orderViewData?.salesOrgCode,
        distributionChannel: orderViewData?.distributionChannelCode,
        division: orderViewData?.divisionCode,
        shipTo: orderViewData?.shipToDisplayText?.split("-")?.[0]?.trim()
      });
      setIsSynced(true);
      setCallResetItemDetails(true);
      setTotalItemsOriginal(orderViewData?.orderItems?.length);

      fetchCashSaleStatus(orderViewData?.soNo, orderViewData?.orderType);

      return result;
    });
  }

  const isNonReusableOrder = checkIsNonReusableOrder(
    getOrderData?.getOrderData?.info
  );

  function mapGetOrderData() {
    const orderLines = getOrderData?.getOrderData?.orderLines?.map(line => ({
      ...line,
      originalSaleText_1: line?.saleText1 || "",
      originalSaleText_2: line?.saleText2 || "",
      originalSaleText_3: line?.saleText3 || "",
      originalSaleText_4: line?.saleText4 || ""
    }));
    return {
      getOrderData: {
        ...getOrderData?.getOrderData,
        orderLines
      }
    };
  }

  return {
    refetchInfo,
    fetchOrderData,
    shouldRecheckBlock,
    getOrderData: mapGetOrderData(),
    isNonReusableOrder,
    changeOrderData,
    setRefetchInfo,
    loadingFetchOrder:
      loadingGetOrder ||
      loadingChangeData ||
      loadingSync ||
      loadingGetCashSaleStatus,
    syncCipOrder,
    refetchChangeOrderData,
    callResetItemDetails,
    setCallResetItemDetails,
    totalItemsOriginal,
    mapGetOrderData,
    cashSaleStatusData,
    cashSaleStatusError
  };
}

export default useCreateOrChangeOrderFetch;
